import { Box, Pagination, Stack } from "@mui/material";
import { APP_PAGE_SIZE_DEFAULT } from "app/appConstants";
import { MprcContainer, MprcIconButtonDropdown, MprcSpinnerIndicator } from "components";
import { MprcFilterBox, MprcFilterCount } from "components/filter";
import { IconSort } from "components/icons";
import { MprcRequestSnackbarErrorReport } from "components/MprcRequestErrorReport";
import { MprcRibbonListCardsExpandedContainer } from "components/MprcRibbonList";
import { MprcRibbonListCardsGridContainer } from "components/MprcRibbonList/MprcRibbonListCardContainers";
import { MprcSearchTextField, MprcSearchTextFieldHandle } from "components/MprcSearchTextField";
import { CardDtoPaginatedList, CarouselCardRenderMode, LmsTrainingOrder } from "generated/CoreApi_v1";
import useDidUpdateEffect from "hooks/useNoInitialEffect";
import TzUtils from "lib/TzUtils";
import { isCanceledError, useCoreApi } from "providers/CoreApiProvider";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CardDtoToCardRibbon } from "views/general/Cards/CardDtoToCardRibbon";
import { useFeedTopicContext } from "views/knowledge/feed/FeedTopic";
import { orderMenuOptions } from "views/lms/student/Catalog/FilterMenus";
import { getClearFilter, ITopicFilterOptions } from "views/lms/topic/FilterCommon";
import { TopicAdvFilters } from "views/lms/topic/TopicAdvFilters";

/**
 * Treinamentos do Tópico.
 * @param props
 */
export const TopicTrainings: React.FC = () => {
    const api = useCoreApi();
    const { t } = useTranslation("lms");
    const { topic, includeDescendants, isHome } = useFeedTopicContext();

    const [viewFilter, setViewFilter] = useState<ITopicFilterOptions>(getClearFilter(includeDescendants, isHome ? LmsTrainingOrder.MostRecent : undefined));
    const [viewData, setViewData] = useState<CardDtoPaginatedList | null>(null);
    const [pageNumber, setPageNumber] = useState<number>(1);

    const abortControllerRef = useRef<AbortController | undefined>(undefined);
    const searchRef = useRef<MprcSearchTextFieldHandle | null>(null);

    ///
    const getTrainings = async () => {
        setViewData(null);

        try {
            abortControllerRef.current?.abort();

            abortControllerRef.current = new AbortController();

            const result = await api.v1.lmsStudent.getTopicTrainings({
                topicId: topic!.topicId,
                includeDescendants: viewFilter.includeDescendants,
                browserTimeZoneId: TzUtils.browserTimezone(),
                filters: viewFilter.advFilters,
                order: viewFilter.order,
                searchText: viewFilter.searchText ?? null,
                pageNumber: pageNumber,
                pageSize: APP_PAGE_SIZE_DEFAULT
            }, { signal: abortControllerRef.current.signal });

            setViewData(result.data);
        } catch (e) {
            if (!isCanceledError(e)) {
                setViewData(null);
                MprcRequestSnackbarErrorReport(e);
            }
        }
        finally {
            abortControllerRef.current = undefined;
        }
    };

    ///
    const resetFilters = () => {
        setViewFilter(getClearFilter(viewFilter.includeDescendants));
        searchRef.current?.reset();
    };

    ////////////////////////////////////////////////////////////////////////////////////////////////
    /// EFFECTS
    ////////////////////////////////////////////////////////////////////////////////////////////////

    const filtered = viewFilter.searchText.length > 0 || viewFilter.advFilters.length > 0;

    ///
    useEffect(() => {
        getTrainings();
    }, [pageNumber, viewFilter, topic]);

    ///
    useDidUpdateEffect(() => {
        setViewFilter(getClearFilter(includeDescendants));
    }, [includeDescendants]);

    ////////////////////////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////////////////////////

    const RenderFilter = (
        <Box mt={2} mb={2}>
            <MprcContainer margin="normal">
                <TopicAdvFilters
                    selectedCommands={viewFilter.advFilters}
                    onChange={(advFilters) => setViewFilter(state => ({ ...state, advFilters }))}
                />
            </MprcContainer>
            <MprcFilterBox>
                <Stack direction="row" spacing={2}>
                    <MprcSearchTextField
                        ref={searchRef}
                        placeholder={t("shared:Search.TextPlaceholder")}
                        confirmMode="immediate"
                        onConfirm={(searchText) => setViewFilter(state => ({ ...state, searchText }))}
                    />

                    <MprcIconButtonDropdown
                        key="orderBy"
                        menuItemCheck={true}
                        icon={<IconSort />}
                        selected={viewFilter.order}
                        items={orderMenuOptions(t)}
                        title={t("shared:Filter.OrderBy")}
                        onChange={(order) => setViewFilter(state => ({ ...state, order }))}
                    />

                </Stack>
            </MprcFilterBox>
        </Box>
    );

    const RenderTrainings = (
        <>
            <MprcContainer margin="dense">
                <MprcFilterCount
                    loading={viewData == null}
                    isEmpty={(viewData?.totalRows ?? 0) == 0}
                    countText={t("Student.Catalog.RecordsFoundText", { count: viewData?.totalRows ?? 0 })}
                    filter={{
                        active: filtered,
                        text: t("Student.Catalog.RecordsFilteredText", { count: viewData?.totalRows ?? 0 }),
                        onResetFilter: resetFilters
                    }}
                />
            </MprcContainer>

            <MprcRibbonListCardsExpandedContainer>

                <MprcRibbonListCardsGridContainer>
                    {
                        CardDtoToCardRibbon(viewData?.items, CarouselCardRenderMode.Default)
                    }
                </MprcRibbonListCardsGridContainer>

                {
                    viewData && viewData.totalPages > 1 &&
                    <Stack justifyItems="center" alignItems="center" sx={{ mt: 2 }}>
                        <Pagination
                            shape="rounded"
                            color="primary"
                            page={viewData.pageNumber}
                            count={viewData.totalPages}
                            onChange={(e, page) => setPageNumber(page)}
                        />
                    </Stack>
                }
            </MprcRibbonListCardsExpandedContainer>
        </>
    );

    return topic && (
        <>
            {RenderFilter}

            {
                viewData == null
                    ? <MprcSpinnerIndicator />
                    : RenderTrainings
            }
        </>
    );
};
